import { createContext } from 'react';

/**
 * The actor and organisation a viewer-scoped cache entry belongs to (ADR 0091).
 *
 * Keys for anything resolved under the viewer's RLS (signed storage URLs,
 * downloaded originals, cited evidence, knowledge lists) carry both, so an
 * entry can never be served to a different member or in a different office,
 * and so `cancelQueries` / `removeQueries` on an identity or organisation
 * change can target exactly the entries that stop being valid.
 *
 * Its own context, filled by `CacheScopeProvider` from the auth and
 * organisation contexts, rather than a read of those two directly: the storage
 * and knowledge hooks that consume it are rendered in isolated tests and
 * stories with neither provider (and with the hook barrels mocked), where the
 * default `null` scope is the right answer.
 */
export interface CacheScope {
  readonly actorId: string | null;
  readonly organizationId: string | null;
  /** Changes immediately when a membership read observes different access. */
  readonly accessEpoch?: number;
  /**
   * The viewer's own role and status in the office (`role|status`), which RLS
   * decides access from; null when unknown. A change is an access change, so
   * state held outside the query cache (an open preview) resets on it.
   */
  readonly accessRevision?: string | null;
}

export const EMPTY_CACHE_SCOPE: CacheScope = {
  actorId: null,
  organizationId: null,
  accessRevision: null,
};

export const CacheScopeContext = createContext<CacheScope>(EMPTY_CACHE_SCOPE);
